'use client'

import { useRouter } from 'next/navigation'
import { parseCookies } from 'nookies'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

import configuration from '~/configuration'

import useSubscriptionPlan from '~/components/Subscription/use-subscription-plan'
import { getAuthCookiesClient } from '~/core/auth/getAuthorizationCookies'
import { AppType } from '~/core/constants/enum'
import { useRouterContext } from '~/lib/next/use-router-context'

import { destroySessionCookiesClient } from '~/core/utilities/catch-api-error'
import type { redirectTo } from '~/lib/next/authUtilities'
import { handleAuthenticationChubFlow } from '~/lib/next/chub/authUtilitiesChub'
import { hasCompleteChubSession } from '~/lib/next/chub/chubSessionCookies'
import useBoundStore from '~/lib/store'
import { getSlugTenantFromCareerHubUrl, isProtectedRoute } from '~/lib/utilities/pathname'

interface ChubFlowProviderProps {
  children: ReactNode
}

function ChubFlowProvider({ children }: ChubFlowProviderProps) {
  const { params, searchParams, pathnameWithoutLocale, asPathWithoutLocale } = useRouterContext()
  const router = useRouter()
  const { user } = useBoundStore()
  const { data: subscriptionData } = useSubscriptionPlan()

  const [isLoading, setIsLoading] = useState(true)

  // Check if current route is protected
  const isPrivateRoute = useMemo(() => {
    return isProtectedRoute(asPathWithoutLocale)
  }, [asPathWithoutLocale])

  // Main authentication effect
  useEffect(() => {
    if (!searchParams || !params || !pathnameWithoutLocale) return

    const authenticationMiddleware = async () => {
      try {
        // For public routes, show component immediately
        if (!isPrivateRoute) {
          setIsLoading(false)
          return
        }

        // Read cookies fresh on each check to handle logout from other tabs
        const cookies = parseCookies()

        if (isPrivateRoute && !hasCompleteChubSession(cookies)) {
          destroySessionCookiesClient(asPathWithoutLocale)
          const currentTenantSlug = getSlugTenantFromCareerHubUrl(asPathWithoutLocale)
          if (currentTenantSlug) {
            window.location.href = configuration.path.careerHub.login(currentTenantSlug)
          } else {
            window.location.href = configuration.path.login
          }
          return
        }

        // Parse user from cookies if not available from store (e.g., after reload)
        // This ensures we always have the latest user info from cookies
        const {
          authCookie: chubAuthCookie,
          currentTenantCookie,
          tenantsCookie
        } = getAuthCookiesClient(cookies, AppType.CHUB_APP)
        let effectiveUser = user

        // Parse currentTenant from cookie (always check cookie for latest currentTenant)
        let parsedTenant = null
        if (currentTenantCookie && currentTenantCookie !== 'undefined') {
          try {
            parsedTenant = JSON.parse(currentTenantCookie)
          } catch (e) {
            // Ignore parse error
          }
        }

        // If user is not in store but cookies exist, try to parse from cookies
        if (!effectiveUser && chubAuthCookie) {
          try {
            const parsedUser = JSON.parse(chubAuthCookie)
            // Parse tenants from tenantsCookie (not from authCookie)
            let parsedTenants = { data: [] }
            if (tenantsCookie) {
              try {
                const tenantsFromCookie = JSON.parse(tenantsCookie)
                // Ensure tenants has the correct structure { data: [...] }
                parsedTenants =
                  tenantsFromCookie?.data || Array.isArray(tenantsFromCookie)
                    ? {
                        data: Array.isArray(tenantsFromCookie)
                          ? tenantsFromCookie
                          : tenantsFromCookie.data
                      }
                    : parsedUser.tenants || { data: [] }
              } catch (e) {
                parsedTenants = parsedUser.tenants || { data: [] }
              }
            } else {
              parsedTenants = parsedUser.tenants || { data: [] }
            }

            // Create a minimal user object from cookie data
            effectiveUser = {
              id: parsedUser.id,
              email: parsedUser.email,
              fullName: parsedUser.fullName,
              tenants: parsedTenants,
              currentTenant: parsedTenant,
              onboarding: parsedUser.onboarding,
              ownTenant: parsedUser.ownTenant
            } as any
          } catch (e) {
            // If parsing fails, effectiveUser remains null
          }
        } else if (effectiveUser && parsedTenant) {
          // If user exists in store but currentTenant might be outdated, merge from cookie
          effectiveUser = {
            ...effectiveUser,
            currentTenant: parsedTenant
          }
        }

        // For private routes, run CareerHub authentication flow
        const planInfo = subscriptionData && {
          isExpired: subscriptionData.isExpired,
          defaultPlan: subscriptionData.defaultPlan
        }

        const tenantSlug = getSlugTenantFromCareerHubUrl(asPathWithoutLocale)

        // Run CareerHub authentication flow
        const flowResult = handleAuthenticationChubFlow({
          cookies,
          user: effectiveUser,
          tenantSlug,
          resolvedUrl: asPathWithoutLocale,
          planInfo
        })

        const redirectResponse = flowResult as ReturnType<typeof redirectTo>

        // Handle redirects
        if (redirectResponse.redirect) {
          const redirectDestination = String(redirectResponse.redirect.destination)

          // Pages that should not redirect (CareerHub login/register)
          const shouldNotRedirect =
            tenantSlug &&
            [
              configuration.path.careerHub.login(tenantSlug),
              configuration.path.careerHub.register(tenantSlug)
            ].some((path) => asPathWithoutLocale?.includes(path))

          // Only redirect if not on no-redirect pages
          if (!shouldNotRedirect) {
            // Determine redirect method based on destination
            const PAGES_USE_WINDOW_LOCATION = [configuration.path.login]
            const useWindowLocation = PAGES_USE_WINDOW_LOCATION.some((url) =>
              redirectDestination.startsWith(url)
            )
            const isRedirectingToLogin =
              redirectDestination.startsWith(configuration.path.login) ||
              (tenantSlug &&
                redirectDestination.startsWith(configuration.path.careerHub.login(tenantSlug)))

            if (isRedirectingToLogin) {
              destroySessionCookiesClient(asPathWithoutLocale)
            }
            if (useWindowLocation || isRedirectingToLogin) {
              window.location.href = redirectDestination
            } else {
              router.push(redirectDestination)
            }
            return
          }
        }

        setIsLoading(false)
      } catch (error) {
        console.error('ChubFlowProvider authentication error:', error)
        setIsLoading(false)
      }
    }

    authenticationMiddleware()
  }, [
    subscriptionData,
    isPrivateRoute,
    searchParams,
    params,
    pathnameWithoutLocale,
    asPathWithoutLocale,
    user,
    router
  ])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return

      const cookies = parseCookies()
      const pathname = window.location.pathname
      const isPrivateRoute = isProtectedRoute(pathname)
      const hasValidAuthState = hasCompleteChubSession(cookies)
      const isStateLoggedIn = hasValidAuthState && !isPrivateRoute
      const isStateLoggedOut = !hasValidAuthState && isPrivateRoute
      if (document.visibilityState === 'visible' && (isStateLoggedIn || isStateLoggedOut)) {
        const tenantSlug = getSlugTenantFromCareerHubUrl(pathname)
        if (isStateLoggedOut && tenantSlug) {
          destroySessionCookiesClient(pathname)
          window.location.href = configuration.path.careerHub.login(tenantSlug)
          return
        }
        if (isStateLoggedOut && !tenantSlug) {
          destroySessionCookiesClient(pathname)
          window.location.href = configuration.path.login
          return
        }
        window.location.href = configuration.path.careerHub.jobs(
          user?.currentTenant?.slug as string
        )
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user?.currentTenant?.slug])

  // Show loading state for private routes during auth check
  if (isLoading && isPrivateRoute) {
    return <></> // CareerHub loading state
  }

  return children
}

export default ChubFlowProvider
